export interface Task<TOptions extends TaskOptions> {
    npmPackage: string;
    name: string;
    description: string;
    category: TaskCategory;
    tags: string[];
    options: TaskOptionMap<TOptions>;
    apply(options: TOptions): Promise<void>;
    // indicates this task is installed in the current project
    isInstalled(): Promise<boolean> | boolean;
    // validate the current project supports this task
    isSupported(): Promise<boolean> | boolean;
}

export interface Integration<TOptions extends TaskOptions>
    extends Task<TOptions> {
    // the tasks this integration integrates with each other
    integrates: string[];
}

export enum TaskCategory {
    UNKNOWN = "UNKNOWN",
    // things like .gitlab-ci.yml
    CI = "CI",
    // things like jest
    TESTING = "TESTING",
    // things like prettier
    TOOLING = "TOOLING",
    // things like rollup
    BUILD = "BUILD",
    // things like redux
    LIBRARY = "LIBRARY",
    // things like creating or modifying README.md
    DOCUMENTATION = "DOCUMENTATION",
    // things like create-react-app
    GENERATOR = "GENERATOR",
    // things like gitignore and husky
    VCS = "VCS",
}

export type TaskOptions = { [name: string]: TaskOptionPrimitiveType };

export type TaskOptionPrimitiveType =
    | string
    | number
    | boolean
    | object
    | Array<any>;

export enum TaskOptionType {
    STRING = "STRING",
    BOOLEAN = "BOOLEAN",
    NUMBER = "NUMBER",
    OBJECT = "OBJECT",
    ARRAY = "ARRAY",
}

export type TaskOptionMap<TOptions extends TaskOptions> = {
    [P in keyof TOptions]: TaskOption<P, TOptions>;
};
export type getTaskOptionType<
    TPrimitive extends TaskOptionPrimitiveType
> = TPrimitive extends string
    ? TaskOptionType.STRING
    : TPrimitive extends number
    ? TaskOptionType.NUMBER
    : TPrimitive extends boolean
    ? TaskOptionType.BOOLEAN
    : TPrimitive extends Array<any>
    ? TaskOptionType.ARRAY
    : TPrimitive extends object
    ? TaskOptionType.OBJECT
    : unknown;

export interface TaskOption<
    TKey extends keyof TOptions,
    TOptions extends TaskOptions
> {
    name: TKey;
    type: getTaskOptionType<TOptions[TKey]>;
    required: boolean;
    defaultValue?: TOptions[TKey];
    validate(
        value: TOptions[TKey],
        options: TOptions,
    ): Promise<boolean | string> | boolean | string;
}
